import { useMemo } from 'react';
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Telemetry } from '@bridgewerk/shared';
import { CHART_AXIS_COLOR, CHART_GRID_COLOR, ChartShell } from './ChartShell.js';
import { ChartTooltip } from './ChartTooltip.js';

const COLOR = '#00C4E8';

export interface FeedRateChartProps {
  readonly points: Telemetry[] | undefined;
  readonly loading?: boolean;
}

export function FeedRateChart({ points, loading }: FeedRateChartProps): JSX.Element {
  const data = useMemo(() => toChartData(points ?? []), [points]);
  return (
    <ChartShell
      title="Feed rate"
      accent={COLOR}
      loading={loading && !points}
      empty={!loading && data.length === 0}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={CHART_GRID_COLOR} strokeDasharray="3 3" />
          <XAxis
            dataKey="t"
            type="number"
            domain={['dataMin', 'dataMax']}
            scale="time"
            tickFormatter={tickTime}
            stroke={CHART_AXIS_COLOR}
            tick={{ fill: CHART_AXIS_COLOR, fontSize: 10 }}
          />
          <YAxis
            stroke={CHART_AXIS_COLOR}
            tick={{ fill: CHART_AXIS_COLOR, fontSize: 10 }}
            width={48}
          />
          <Tooltip content={<ChartTooltip unit="mm/min" />} />
          <Line
            type="monotone"
            dataKey="v"
            stroke={COLOR}
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
          <Brush
            dataKey="t"
            height={18}
            stroke={CHART_AXIS_COLOR}
            travellerWidth={8}
            fill="transparent"
            tickFormatter={tickTime}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartShell>
  );
}

function toChartData(points: Telemetry[]): { t: number; v: number }[] {
  return points
    .map((p) => ({ t: new Date(p.timestamp).getTime(), v: p.feed_rate }))
    .sort((a, b) => a.t - b.t);
}

function tickTime(value: number): string {
  const d = new Date(value);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
